export interface AIUserMatch {
    user: any;
    confidence: number;
}

export interface AIAssignment {
    booth: string;
    user: any;
    spokenName: string;
    confidence: number;
}

export interface AIParseResult {
    success: boolean;
    assignments: AIAssignment[];
    errors: string[];
}

/**
 * Normalize text
 */
const normalizeText = (value: string): string => {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, " ")
        .trim();
};

/**
 * Small phonetic normalization.
 *
 * Examples:
 *
 * Aditya  -> adita
 * Adithya -> adita
 *
 * Shashank -> shasank
 */
const phoneticNormalize = (value: string): string => {
    return normalizeText(value)
        .replace(/th/g, "t")
        .replace(/dh/g, "d")
        .replace(/sh/g, "s")
        .replace(/ch/g, "c")
        .replace(/ph/g, "f")
        .replace(/ck/g, "k")
        .replace(/kh/g, "k")
        .replace(/gh/g, "g")
        .replace(/aa/g, "a")
        .replace(/ee/g, "i")
        .replace(/ii/g, "i")
        .replace(/oo/g, "u")
        .replace(/uu/g, "u");
};
/**
 * Levenshtein distance
 */
const levenshteinDistance = (a: string, b: string): number => {
    const matrix: number[][] = [];

    for (let i = 0; i <= b.length; i++) {
        matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b[i - 1] === a[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
            }
        }
    }

    return matrix[b.length][a.length];
};

/**
 * Similarity percentage
 */
const similarity = (first: string, second: string): number => {
    if (!first || !second) {
        return 0;
    }

    if (first === second) {
        return 100;
    }

    const distance = levenshteinDistance(first, second);

    const maxLength = Math.max(first.length, second.length);

    if (!maxLength) {
        return 0;
    }

    return Math.round((1 - distance / maxLength) * 100);
};

/**
 * Match spoken name against actual users.
 *
 * Example:
 *
 * Spoken: "Aditya"
 *
 * Actual user:
 * "Adithya"
 *
 * Result:
 * Adithya - 90%+
 */
export const findBestUserMatch = (spokenName: string, users: any[]): AIUserMatch | null => {
    const spoken = normalizeText(spokenName);

    const spokenPhonetic = phoneticNormalize(spokenName);

    if (!spoken) {
        return null;
    }

    let bestMatch: AIUserMatch | null = null;

    for (const user of users) {
        if (!user?.name) {
            continue;
        }

        const actualName = user.name;

        const normalScore = similarity(spoken, normalizeText(actualName));

        const phoneticScore = similarity(spokenPhonetic, phoneticNormalize(actualName));

        const score = Math.max(normalScore, phoneticScore);

        if (!bestMatch || score > bestMatch.confidence) {
            bestMatch = {
                user,
                confidence: score,
            };
        }
    }

    return bestMatch;
};

/**
 * Remove common speech words.
 */
const cleanCommand = (command: string): string => {
    let text = command.replace(/[.?!]/g, " ").replace(/\s+/g, " ").trim();

    /**
     * Speech recognition can sometimes hear:
     *
     * "Azeem booth 227 to Mohan Kumar"
     *
     * when the user actually said:
     *
     * "Assign booth 227 to Mohan Kumar"
     *
     * So remove everything before the first booth/number.
     */
    const firstNumber = text.search(/\d/);

    if (firstNumber >= 0) {
        text = text.substring(firstNumber);
    }

    return text.trim();
};

/**
 * Parse one command.
 *
 * Supported:
 *
 * 162 to Aditya
 * booth 162 to Aditya
 * 162 for Aditya
 */
const parseSingleAssignment = (text: string) => {
    const match = text.match(/(?:booths?\s*)?(\d{1,5}(?:\s*,\s*\d{1,5})*)\s+(?:to|for)\s+(.+)/i);

    if (!match) {
        return null;
    }

    const boothText = match[1];

    const spokenName = match[2]
        .trim()
        .replace(/^(and|then|also)\s+/i, "")
        .replace(/\s+(and|then|also)$/i, "")
        .trim();

    const booths = boothText.match(/\d+/g) || [];

    return {
        booths,
        spokenName,
    };
};

/**
 * Main AI assignment parser.
 *
 * Examples:
 *
 * Assign 162 to Aditya
 *
 * Assign 162 to Aditya and 123 to Ramesh
 *
 * Assign booth 162 to Aditya,
 * booth 123 to Ramesh
 *
 * Give 162 to Aditya and 163 to Mohan
 */
export const parseAssignmentCommand = (command: string, users: any[]): AIParseResult => {
    const cleaned = cleanCommand(command);

    console.log("🤖 AI RAW COMMAND:", command);

    console.log("🧹 AI CLEANED COMMAND:", cleaned);

    if (!cleaned) {
        return {
            success: false,
            assignments: [],
            errors: ["No assignment command detected."],
        };
    }

    /**
     * Split whenever another booth number
     * starts a new assignment.
     *
     * Example:
     *
     * 162 to Aditya and 123 to Ramesh
     *
     * becomes:
     *
     * 162 to Aditya
     * 123 to Ramesh
     */
    const chunks = cleaned
        .replace(/\bbooths?\b/gi, "")
        .split(/(?=\d{1,5}\s+(?:to|for)\s+)/gi)
        .map((item) => item.replace(/^(and|then|also|,)\s*/i, "").trim())
        .filter(Boolean);

    console.log("🧩 AI ASSIGNMENT CHUNKS:", chunks);

    const assignments: AIAssignment[] = [];

    const errors: string[] = [];

    for (const chunk of chunks) {
        const parsed = parseSingleAssignment(chunk);

        if (!parsed) {
            continue;
        }

        console.log("📦 PARSED:", parsed);

        const userMatch = findBestUserMatch(parsed.spokenName, users);

        if (!userMatch) {
            errors.push(`Could not find user "${parsed.spokenName}".`);

            continue;
        }

        /**
         * Don't silently choose a completely
         * unrelated user.
         */
        if (userMatch.confidence < 55) {
            errors.push(`Could not confidently match "${parsed.spokenName}".`);

            continue;
        }

        for (const booth of parsed.booths) {
            assignments.push({
                booth,
                user: userMatch.user,
                spokenName: parsed.spokenName,
                confidence: userMatch.confidence,
            });
        }
    }

    /**
     * Remove duplicate booth + user pairs.
     */
    const uniqueAssignments = assignments.filter(
        (assignment, index, array) =>
            array.findIndex(
                (item) => item.booth === assignment.booth && item.user?.id === assignment.user?.id
            ) === index
    );

    console.log("✅ AI FINAL ASSIGNMENTS:", uniqueAssignments);

    return {
        success: uniqueAssignments.length > 0 && errors.length === 0,

        assignments: uniqueAssignments,

        errors,
    };
};
